export type SmartGuideBrowserLocationSource = "gps" | "ip";

export type SmartGuideBrowserLocation = {
    latitude: number;
    longitude: number;
    accuracy?: number;
    heading?: number;
    source: SmartGuideBrowserLocationSource;
    timestamp: number;
};

export const browserLocationChangedEventName = "smartguide:user-location-changed";
export const browserLocationStorageKey = "smartguide.browser-location.v1";
export const browserLocationDefaultMaxAgeMs = 60 * 60 * 1000;

type ReadBrowserLocationOptions = {
    maxAgeMs?: number;
    sources?: SmartGuideBrowserLocationSource[];
};

export function readRecentBrowserLocation(options: ReadBrowserLocationOptions = {}): SmartGuideBrowserLocation | undefined {
    const storage = browserSessionStorage();
    if (!storage) {
        return undefined;
    }

    const location = parseBrowserLocation(storage.getItem(browserLocationStorageKey));
    if (!location) {
        return undefined;
    }

    const maxAgeMs = options.maxAgeMs ?? browserLocationDefaultMaxAgeMs;
    if (Date.now() - location.timestamp > maxAgeMs) {
        return undefined;
    }

    if (options.sources?.length && !options.sources.includes(location.source)) {
        return undefined;
    }

    return location;
}

export function rememberBrowserLocation(location: SmartGuideBrowserLocation, options: { notify?: boolean } = {}): void {
    if (!isBrowserLocation(location)) {
        return;
    }

    browserSessionStorage()?.setItem(browserLocationStorageKey, JSON.stringify(location));
    if (options.notify !== false) {
        document.dispatchEvent(new CustomEvent<SmartGuideBrowserLocation>(browserLocationChangedEventName, {
            detail: location
        }));
    }
}

export function browserLocationFromGeolocationPosition(position: GeolocationPosition): SmartGuideBrowserLocation | undefined {
    const latitude = Number(position.coords.latitude);
    const longitude = Number(position.coords.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return undefined;
    }

    const accuracy = Number(position.coords.accuracy);
    const positionTimestamp = Number(position.timestamp);
    const heading = position.coords.heading;
    return {
        latitude,
        longitude,
        accuracy: Number.isFinite(accuracy) && accuracy > 0 ? accuracy : undefined,
        heading: typeof heading === "number" && Number.isFinite(heading) ? heading : undefined,
        source: "gps",
        timestamp: Number.isFinite(positionTimestamp) && positionTimestamp > 0
            ? positionTimestamp
            : Date.now()
    };
}

export function browserLocationFromEvent(event: Event): SmartGuideBrowserLocation | undefined {
    const detail = (event as CustomEvent<unknown>).detail;
    return isBrowserLocation(detail) ? detail : undefined;
}

function parseBrowserLocation(value: string | null): SmartGuideBrowserLocation | undefined {
    if (!value) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(value) as unknown;
        return isBrowserLocation(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function isBrowserLocation(value: unknown): value is SmartGuideBrowserLocation {
    if (!value || typeof value !== "object") {
        return false;
    }

    const location = value as SmartGuideBrowserLocation;
    return Number.isFinite(location.latitude) &&
        Number.isFinite(location.longitude) &&
        Number.isFinite(location.timestamp) &&
        (location.source === "gps" || location.source === "ip") &&
        (location.accuracy === undefined || Number.isFinite(location.accuracy)) &&
        (location.heading === undefined || Number.isFinite(location.heading));
}

function browserSessionStorage(): Storage | undefined {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}
